const BASE_URL = 'https://boleum.com/';
const DEFAULT_IMAGE = `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`;
const SITE_NAME = 'BoleumLearn - Transform your career with expert-led online training';
const DEFAULT_DESCRIPTION = 'Master new skills with BoleumLearn. Access premium courses, expert instructors, and flexible learning paths for students and professionals.';

const createTitle = (pageTitle) => {
  return pageTitle ? `${pageTitle} - ${SITE_NAME}` : SITE_NAME;
};

export const SEO_CONFIG = {
  // Home page
  '/': {
    title: SITE_NAME,
    description: 'Learn new skills with expert-led courses. Flexible learning paths for students and professionals. Start your learning journey today!',
    keywords: 'online courses, e-learning, learn online, education platform, skill development',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  // Authentication pages (noindex - don't want these in search results)
  '/login': {
    title: createTitle('Login to Your Account'),
    description: 'Sign in to access your courses, dashboard, and learning materials on BoleumLearn.',
    keywords: 'login, sign in, account access',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/register': {
    title: createTitle('Create Your Free Account'),
    description: 'Join BoleumLearn today! Create your free account to access courses, track progress, and start learning.',
    keywords: 'register, sign up, create account, join',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/change-password': {
    title: createTitle('Change Your Password'),
    description: 'Securely update your BoleumLearn account password.',
    keywords: 'change password, update password, account security',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/reset-password': {
    title: createTitle('Reset Your Password'),
    description: 'Forgot your password? Reset it securely to regain access to your BoleumLearn account.',
    keywords: 'reset password, forgot password, password recovery',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // Course catalog
  '/courses': {
    title: createTitle('Course Catalog - All Available Courses'),
    description: 'Browse our complete catalog of courses across various categories. Find the perfect course to advance your skills and career.',
    keywords: 'course catalog, all courses, course directory, online courses',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  // Categories
  '/categories': {
    title: createTitle('Course Categories'),
    description: 'Explore courses organized by category. Find learning paths in technology, business, design, and more.',
    keywords: 'course categories, learning categories, subject areas',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  '/categories/:categorySlug': {
    title: createTitle('Courses by Category'),
    description: 'Browse courses in this specific category. Expert-led courses with flexible learning schedules.',
    keywords: 'category courses, subject courses, online learning',
    image: DEFAULT_IMAGE,
    robots: 'index, follow'
  },
  
  '/course/:slug/:id': {
    title: createTitle('Course Details'),
    description: 'Detailed information about this course including curriculum, instructor, reviews, and enrollment options.',
    keywords: 'course details, course information, course syllabus, course reviews',
    image: DEFAULT_IMAGE,
    robots: 'index, follow'
  },
  
  // Legal & Info pages
  '/contact': {
    title: createTitle('Contact Us'),
    description: 'Get in touch with our support team. We\'re here to help with any questions about courses, accounts, or technical issues.',
    keywords: 'contact us, support, help, customer service',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  '/about': {
    title: createTitle('About BoleumLearn'),
    description: 'Learn about our mission, vision, and team. Discover why thousands of students trust BoleumLearn for their online education.',
    keywords: 'about us, our story, mission, vision, team',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  '/help-support': {
    title: createTitle('Help & Support Center'),
    description: 'Find answers to common questions, troubleshooting guides, and support resources for BoleumLearn users.',
    keywords: 'help center, support, faq, troubleshooting, user guide',
    image: `${BASE_URL}static/media/boleumlogo.f78456cd4efb8df28665.png`,
    robots: 'index, follow'
  },
  
  '/terms': {
    title: createTitle('Terms and Conditions'),
    description: 'Official terms and conditions for using the BoleumLearn platform and services.',
    keywords: 'terms of service, terms and conditions, legal terms',
    image: DEFAULT_IMAGE,
    robots: 'index, follow'
  },
  
  '/privacy': {
    title: createTitle('Privacy Policy'),
    description: 'Learn how we collect, use, and protect your personal information on the BoleumLearn platform.',
    keywords: 'privacy policy, data protection, personal information, gdpr',
    image: DEFAULT_IMAGE,
    robots: 'index, follow'
  },
  
  // Payment verification
  '/payment-verification': {
    title: createTitle('Payment Verification'),
    description: 'Verify your course payment and complete your enrollment process.',
    keywords: 'payment verification, payment confirmation, enrollment',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // Student onboarding
  '/student/onboarding': {
    title: createTitle('Student Onboarding'),
    description: 'Complete your student profile setup and get started with your learning journey on BoleumLearn.',
    keywords: 'student onboarding, profile setup, getting started',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // Instructor onboarding
  '/instructor/onboarding': {
    title: createTitle('Instructor Onboarding'),
    description: 'Complete your instructor profile setup and start creating courses on BoleumLearn.',
    keywords: 'instructor onboarding, teach online, become instructor',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/pending-verification': {
    title: createTitle('Instructor Verification Pending'),
    description: 'Your instructor application is under review. Learn about the verification process.',
    keywords: 'instructor verification, pending approval, instructor status',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // === PROTECTED PAGES (Most should be noindex) ===
  
  // Student dashboard and pages
  '/student/dashboard': {
    title: createTitle('Student Dashboard'),
    description: 'Your personalized learning dashboard with course progress, notifications, and recommendations.',
    keywords: 'student dashboard, learning dashboard, course progress',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/my-courses': {
    title: createTitle('My Courses'),
    description: 'Access all your enrolled courses, track progress, and continue your learning journey.',
    keywords: 'my courses, enrolled courses, course progress',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/wishlist': {
    title: createTitle('Course Wishlist'),
    description: 'View and manage your saved courses for future enrollment.',
    keywords: 'wishlist, saved courses, course favorites',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/billings': {
    title: createTitle('Billing & Payments'),
    description: 'Manage your subscription, view payment history, and update billing information.',
    keywords: 'billing, payments, subscription, payment history',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/notifications': {
    title: createTitle('Notifications'),
    description: 'View your course notifications, announcements, and important updates.',
    keywords: 'notifications, alerts, announcements',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/messages': {
    title: createTitle('Messages'),
    description: 'Communicate with instructors and support through your messaging center.',
    keywords: 'messages, communication, inbox',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/discussion/:id': {
    title: createTitle('Course Discussion'),
    description: 'Participate in course discussions and Q&A with instructors and fellow students.',
    keywords: 'discussion, course forum, q&a, student interaction',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/lesson/:id': {
    title: createTitle('Lesson Content'),
    description: 'Access your lesson materials, videos, and learning resources.',
    keywords: 'lesson, course content, learning materials',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/quiz/:id': {
    title: createTitle('Quiz Assessment'),
    description: 'Complete your course quiz to test your knowledge and track progress.',
    keywords: 'quiz, assessment, knowledge test, course evaluation',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/assignment/:id': {
    title: createTitle('Course Assignment'),
    description: 'Complete and submit your course assignment for instructor review.',
    keywords: 'assignment, homework, course work, submission',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/achivements': { // Note: typo in route, but keeping as-is
    title: createTitle('Achievements & Badges'),
    description: 'View your earned badges, certificates, and learning achievements.',
    keywords: 'achievements, badges, certificates, accomplishments',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/edit-profile': {
    title: createTitle('Edit Student Profile'),
    description: 'Update your personal information, preferences, and learning goals.',
    keywords: 'edit profile, student profile, account settings',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/schedule': {
    title: createTitle('Learning Schedule'),
    description: 'View and manage your course schedule and upcoming deadlines.',
    keywords: 'schedule, calendar, deadlines, course timeline',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/student/progress': {
    title: createTitle('Learning Progress'),
    description: 'Track your overall learning progress, completed courses, and skill development.',
    keywords: 'progress tracking, learning analytics, course completion',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // Instructor dashboard and pages
  '/instructor/dashboard': {
    title: createTitle('Instructor Dashboard'),
    description: 'Manage your courses, students, revenue, and analytics from your instructor dashboard.',
    keywords: 'instructor dashboard, teaching dashboard, course management',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/my-courses': {
    title: createTitle('My Courses'),
    description: 'Manage all your created courses, view enrollments, and update course content.',
    keywords: 'instructor courses, course management, teaching',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/course/builder': {
    title: createTitle('Course Builder'),
    description: 'Create and design new courses with our intuitive course builder tool.',
    keywords: 'course builder, create course, course creation, curriculum design',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/course/edit-course/:id': {
    title: createTitle('Edit Course'),
    description: 'Update and manage your course content, settings, and curriculum.',
    keywords: 'edit course, course management, update course',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/notifications': {
    title: createTitle('Instructor Notifications'),
    description: 'View notifications about your courses, student activities, and platform updates.',
    keywords: 'instructor notifications, teaching alerts, course updates',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/messages': {
    title: createTitle('Instructor Messages'),
    description: 'Communicate with your students and support team through your messaging center.',
    keywords: 'instructor messages, student communication, teaching inbox',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/discussion/:id': {
    title: createTitle('Instructor Discussion'),
    description: 'Manage and participate in course discussions with your students.',
    keywords: 'instructor discussion, course forum management, student interaction',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/edit-profile': {
    title: createTitle('Edit Instructor Profile'),
    description: 'Update your instructor profile, bio, and teaching credentials.',
    keywords: 'instructor profile, edit profile, teaching credentials',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/assignment': {
    title: createTitle('Assignment Dashboard'),
    description: 'Manage and review student assignments for your courses.',
    keywords: 'assignment management, student submissions, grading',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/grade/:id': {
    title: createTitle('Assignment Grading'),
    description: 'Review and grade student assignments with detailed feedback.',
    keywords: 'grading, assignment review, student feedback, assessment',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/create-assignment/:lessonId': {
    title: createTitle('Create Assignment'),
    description: 'Create new assignments for your course lessons to assess student learning.',
    keywords: 'create assignment, assignment creation, course assessment',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/edit-assignment/:lessonId/:assignmentId': {
    title: createTitle('Edit Assignment'),
    description: 'Update and modify existing assignments for your course lessons.',
    keywords: 'edit assignment, assignment management, course assessment',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/create-quiz/:lessonId': {
    title: createTitle('Create Quiz'),
    description: 'Design and create quizzes to test student knowledge in your courses.',
    keywords: 'create quiz, quiz builder, knowledge assessment',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/edit-quiz/:assignmentId/:quizId': {
    title: createTitle('Edit Quiz'),
    description: 'Modify and update existing quizzes for your course lessons.',
    keywords: 'edit quiz, quiz management, assessment editing',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/analytics': {
    title: createTitle('Instructor Analytics'),
    description: 'View detailed analytics about your courses, student engagement, and performance metrics.',
    keywords: 'instructor analytics, course analytics, student engagement, teaching metrics',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/revenue': {
    title: createTitle('Revenue Dashboard'),
    description: 'Track your earnings, payouts, and financial performance as an instructor.',
    keywords: 'instructor revenue, earnings, payouts, financial dashboard',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/instructor/media/library': {
    title: createTitle('Media Library'),
    description: 'Manage and organize your course media files, images, and video content.',
    keywords: 'media library, course media, file management, content library',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // Admin pages (all noindex)
  '/admin/cloud/dashboard': {
    title: createTitle('Admin Dashboard'),
    description: 'Platform administration dashboard for managing users, courses, and system settings.',
    keywords: 'admin dashboard, platform management, system administration',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/users': {
    title: createTitle('User Management'),
    description: 'Manage platform users, roles, and account settings.',
    keywords: 'user management, account administration, user roles',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/courses': {
    title: createTitle('Course Management'),
    description: 'Administer all platform courses, including approvals, categories, and content.',
    keywords: 'course management, course administration, content management',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/categories': {
    title: createTitle('Category Management'),
    description: 'Manage course categories and organizational structure.',
    keywords: 'category management, course categories, taxonomy management',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/approvals': {
    title: createTitle('Course Approvals'),
    description: 'Review and approve instructor-submitted courses for platform publication.',
    keywords: 'course approvals, content review, quality control',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/withdrawal': {
    title: createTitle('Withdrawal Management'),
    description: 'Process and manage instructor withdrawal requests and payments.',
    keywords: 'withdrawal management, payment processing, instructor payouts',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/support': {
    title: createTitle('Support Tickets'),
    description: 'Manage and resolve user support tickets and platform issues.',
    keywords: 'support tickets, customer support, issue resolution',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/analytics': {
    title: createTitle('Platform Analytics'),
    description: 'View comprehensive platform analytics, user metrics, and business intelligence.',
    keywords: 'platform analytics, business intelligence, user metrics, performance data',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  '/admin/cloud/settings': {
    title: createTitle('Platform Settings'),
    description: 'Configure platform-wide settings, policies, and system configurations.',
    keywords: 'platform settings, system configuration, admin settings',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  },
  
  // === CATCH-ALL & ERROR HANDLING ===
  
  // 404 page
  '/404': {
    title: createTitle('Page Not Found'),
    description: 'Sorry, the page you are looking for does not exist. Return to our homepage to continue your learning journey.',
    keywords: '404, not found, error, page missing',
    image: DEFAULT_IMAGE,
    robots: 'noindex, follow'
  }
};

export const DEFAULT_SEO = {
  title: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  keywords: 'online learning, onilne, training, oil & gas training, technical courses, advanced course, courses, e-learning platform, skill development, education',
  image: DEFAULT_IMAGE,
  robots: 'index, follow'
};